import classNames from "classnames";
import { Entry } from "contentful";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { AbstractIntlMessages, useTranslations } from "next-intl";
import { useEffect } from "react";
import { useSetRecoilState } from "recoil";

import { countryDialogOpenedState } from "~/atoms/countryDialog";
import { useBreakpoint } from "~/contexts/breakpoint";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import crossCountryUtils from "~/utils/crossCountry-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";
import useScrollDirection from "~/utils/useScrollDirection";

import Container from "../common/container";
import Icon from "../common/icon";
import styles from "./header.module.scss";

type Props = {
  entry: Entry<unknown>;
  navColor: Editorials.navColor;
  inPpd?: boolean;
  searchSuggestions?: string[];
  isHomepage?: boolean;
  messages?: AbstractIntlMessages;
};

export default function Header(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const otherScrollDetector = useScrollDirection({});
  const { dir, y } = useScrollDirection({
    initialDirection: "up",
    thresholdPixels:
      breakpoint == "desktop"
        ? Constants.HEADER_HEIGHT_DESKTOP
        : Constants.HEADER_HEIGHT_MOBILE,
  });

  const country = crossCountryUtils.getCurrentCountryCode(router);
  const setCountryDialogOpenedState = useSetRecoilState(
    countryDialogOpenedState
  );

  function openCountryDialog() {
    setCountryDialogOpenedState(true);
  }

  useEffect(() => {
    if (y >= Constants.HEADER_HEIGHT_DESKTOP) {
      document.body.style.setProperty("--top-menu", "80px");
      document.body.style.setProperty("--header-menu", "0");
    } else {
      document.body.style.setProperty(
        "--top-menu",
        `${Constants.HEADER_HEIGHT_DESKTOP}px`
      );
      document.body.style.setProperty("--header-menu", "80px");
    }
  }, [y]);

  assertEditorialType<Editorials.TopBar>(props.entry, ["navigation"]);

  const storeLink = props.entry.fields.storeLink;
  const inspectorModeStoreLink = contentfulUtils.useInspectorMode(storeLink);

  return (
    <header
      id="header"
      className={classNames(
        styles.header,
        { [styles.scrolledTotal]: dir == "down" },
        { [styles.scrolledTop]: y >= Constants.HEADER_HEIGHT_DESKTOP },
        { [styles.scrolledNavBg]: otherScrollDetector.y >= 1 },
        { [styles.inPdp]: props.inPpd },
        styles[props.navColor]
      )}
    >
      {/* <TopBar entry={props.entry.fields.topBar} /> */}
      <div className={styles.main}>
        <Container className={styles.mainContainer}>
          {storeLink && isActiveEntry(storeLink) && (
            <Link
              {...inspectorModeStoreLink?.getProps("text")}
              prefetch={false}
              href={textUtils.sanitizeContentfulUrl(storeLink, router)}
              target={storeLink.fields.openOnANewTab ? "_blank" : undefined}
              className={styles.topBarLink}
            >
              {storeLink.fields.icon &&
                isActiveEntry(storeLink.fields.icon) && (
                  <Icon name={storeLink.fields.icon.fields.svgIconId} />
                )}
              <span>{storeLink.fields.text}</span>
            </Link>
          )}
          {/* which contains search */}
          {/* <MenuMobile
            nav={props.entry}
            messages={props.messages}
            searchSuggestions={props.searchSuggestions}
          /> */}
          <div className={styles.logoWrapper}>
            {props.isHomepage ? (
              <h1>
                <Link href="/" className={styles.center}>
                  <Icon name="kiko" height={breakpoint == "mobile" ? 24 : 28} />
                </Link>
              </h1>
            ) : (
              <Link href="/" className={styles.center}>
                <Icon name="kiko" height={breakpoint == "mobile" ? 24 : 28} />
              </Link>
            )}
          </div>
          <button
            className={styles.topBarLink}
            onClick={() => openCountryDialog()}
          >
            <>
              <span>
                {router.locale
                  ?.replace(/(\w*)\s*-\s*(\w*)/, "$2 | $1")
                  .toUpperCase()}
              </span>
              <Image
                unoptimized
                width={16}
                height={16}
                alt={country}
                src={`/next/assets/flags/${country.toLowerCase()}.svg`}
              />
            </>
          </button>
        </Container>
      </div>
      {/* {breakpoint == "desktop" && (
        <Navigation nav={props.entry} className={styles.headerNav} />
      )}
      <SeoNavigation nav={props.entry} /> */}
    </header>
  );
}
